import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { API_URL, formatCurrency } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { toast } from "sonner";
import {
    Save,
    Loader2,
    Zap,
    Crown,
    Building2,
    RefreshCw,
} from "lucide-react";

const PLAN_ICONS = { basic: Zap, pro: Crown, enterprise: Building2 };

export const AdminPlansPage = () => {
    const { token } = useAuth();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);

    const fetchPlans = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/platform/plans`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) setPlans(await res.json());
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchPlans(); }, [fetchPlans]);

    const updatePlanField = (planId, field, value) => {
        setPlans((prev) =>
            prev.map((p) => p.plan_id === planId ? { ...p, [field]: value } : p)
        );
    };

    const updatePlanLimit = (planId, key, value) => {
        setPlans((prev) =>
            prev.map((p) =>
                p.plan_id === planId
                    ? { ...p, limits: { ...p.limits, [key]: parseInt(value) || 0 } }
                    : p
            )
        );
    };

    const handleSave = async (plan) => {
        setSaving(plan.plan_id);
        try {
            const res = await fetch(`${API_URL}/platform/plans/${plan.plan_id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    name: plan.name,
                    description: plan.description,
                    price_monthly: parseFloat(plan.price_monthly),
                    price_yearly: parseFloat(plan.price_yearly),
                    features: plan.features,
                    limits: plan.limits,
                    is_active: plan.is_active,
                }),
            });
            if (!res.ok) throw new Error("Update failed");
            toast.success(`Plan ${plan.name} mis a jour`);
        } catch (e) {
            toast.error("Erreur lors de la sauvegarde");
        } finally {
            setSaving(null);
        }
    };

    const updateFeatures = (planId, featuresStr) => {
        const features = featuresStr.split(",").map((f) => f.trim()).filter(Boolean);
        setPlans((prev) =>
            prev.map((p) => p.plan_id === planId ? { ...p, features } : p)
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in" data-testid="admin-plans-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">
                        Gestion des plans
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Modifiez les prix, limites et fonctionnalites des plans
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchPlans} data-testid="refresh-plans-btn">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {plans.map((plan) => {
                    const Icon = PLAN_ICONS[plan.plan_id] || Zap;
                    return (
                        <Card key={plan.plan_id} data-testid={`admin-plan-card-${plan.plan_id}`}>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <Icon className="w-5 h-5 text-primary" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg">{plan.name}</CardTitle>
                                            <CardDescription className="text-xs">{plan.plan_id}</CardDescription>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Label className="text-xs">Actif</Label>
                                        <Switch
                                            checked={plan.is_active}
                                            onCheckedChange={(v) => updatePlanField(plan.plan_id, "is_active", v)}
                                            data-testid={`plan-active-${plan.plan_id}`}
                                        />
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label className="text-xs">Nom</Label>
                                    <Input
                                        value={plan.name}
                                        onChange={(e) => updatePlanField(plan.plan_id, "name", e.target.value)}
                                        data-testid={`plan-name-${plan.plan_id}`}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Description</Label>
                                    <Input
                                        value={plan.description}
                                        onChange={(e) => updatePlanField(plan.plan_id, "description", e.target.value)}
                                    />
                                </div>
                                <Separator />
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">Prix mensuel (EUR)</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={plan.price_monthly}
                                            onChange={(e) => updatePlanField(plan.plan_id, "price_monthly", e.target.value)}
                                            data-testid={`plan-price-monthly-${plan.plan_id}`}
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Prix annuel (EUR)</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={plan.price_yearly}
                                            onChange={(e) => updatePlanField(plan.plan_id, "price_yearly", e.target.value)}
                                            data-testid={`plan-price-yearly-${plan.plan_id}`}
                                        />
                                    </div>
                                </div>
                                <Separator />
                                <div className="space-y-3">
                                    <Label className="text-xs font-semibold">Limites</Label>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div>
                                            <Label className="text-[10px] text-muted-foreground">Utilisateurs</Label>
                                            <Input
                                                type="number"
                                                value={plan.limits?.users ?? 0}
                                                onChange={(e) => updatePlanLimit(plan.plan_id, "users", e.target.value)}
                                                className="h-8 text-sm"
                                                data-testid={`plan-limit-users-${plan.plan_id}`}
                                            />
                                            <p className="text-[10px] text-muted-foreground mt-0.5">-1 = illimite</p>
                                        </div>
                                        <div>
                                            <Label className="text-[10px] text-muted-foreground">Projets</Label>
                                            <Input
                                                type="number"
                                                value={plan.limits?.projects ?? 0}
                                                onChange={(e) => updatePlanLimit(plan.plan_id, "projects", e.target.value)}
                                                className="h-8 text-sm"
                                            />
                                            <p className="text-[10px] text-muted-foreground mt-0.5">-1 = illimite</p>
                                        </div>
                                        <div>
                                            <Label className="text-[10px] text-muted-foreground">Stockage (Go)</Label>
                                            <Input
                                                type="number"
                                                value={plan.limits?.storage_gb ?? 0}
                                                onChange={(e) => updatePlanLimit(plan.plan_id, "storage_gb", e.target.value)}
                                                className="h-8 text-sm"
                                            />
                                            <p className="text-[10px] text-muted-foreground mt-0.5">-1 = illimite</p>
                                        </div>
                                    </div>
                                </div>
                                <Separator />
                                <div>
                                    <Label className="text-xs">Fonctionnalites (separees par virgule)</Label>
                                    <Input
                                        value={plan.features?.join(", ") || ""}
                                        onChange={(e) => updateFeatures(plan.plan_id, e.target.value)}
                                        placeholder="CRM, Projets, Factures..."
                                        data-testid={`plan-features-${plan.plan_id}`}
                                    />
                                </div>
                                <Button
                                    className="w-full"
                                    onClick={() => handleSave(plan)}
                                    disabled={saving === plan.plan_id}
                                    data-testid={`save-plan-${plan.plan_id}`}
                                >
                                    {saving === plan.plan_id ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Save className="w-4 h-4 mr-2" />
                                    )}
                                    Enregistrer
                                </Button>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
};
